import { useCallback, useEffect, useState } from "react";
import type { Authorization, Courier, CourierBoard, CourierMode, Order, Settlement, Statement } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatTime } from "../../lib/time";
import { addressOf } from "../orders/order-view";
import { boardColumns, courierStatus, kmText, payPill, type BoardDelivery } from "./board";
import { CourierBonDialog } from "./CourierBonDialog";
import { SettleDialog } from "./SettleDialog";
import { TrackDialog } from "./TrackDialog";
import { UndoSettlementDialog } from "./UndoSettlementDialog";

interface Props {
  /** Goes up when an order, a courier or a settlement changed on any device: the board reloads. */
  revision: number;
}

const MODES: readonly CourierMode[] = ["self", "till"];

type Open =
  | { kind: "bon"; statement: Statement | Settlement; at: string }
  | { kind: "settle"; statement: Statement; authorization: Authorization }
  | { kind: "undo"; settlement: Settlement; authorization: Authorization }
  | { kind: "track"; token: string };

/**
 * Koeriers (prototype renderCouriers, Ctrl+3): "Te leveren" and a column per courier with his rides.
 * The till gives a delivery to a courier (→ Mehmet), takes it back (Terug) or marks it delivered
 * (Geleverd, paid in cash at the door); a ride on its way shows the expected arrival and the
 * customer's page. Per courier: what he delivered and did not settle, the afrekenbon (no code) and
 * Afrekenen with the code of a manager or the owner. Below: the way of working and today's settlements.
 */
export function CouriersScreen({ revision }: Props) {
  const { api, authorize, report, toast } = useTill();
  const text = t.couriers;
  const [board, setBoard] = useState<CourierBoard | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<Open | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) =>
      api
        .request<CourierBoard>("GET", "/device/couriers/board", { retry: true, ...(signal ? { signal } : {}) })
        .then(setBoard)
        .catch((error: unknown) => {
          if (!signal?.aborted) report(error);
        }),
    [api, report],
  );
  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [load, revision]);

  /** One action on the board, then the board as it is now (the live update follows elsewhere). */
  const act = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
      void load();
    }
  };

  const assign = (order: Order, courier: Courier | null) =>
    act(async () => {
      await api.request("POST", "/device/deliveries/courier", {
        body: { orderIds: [order.id], courierId: courier?.id ?? null },
      });
      if (courier) toast(text.assigned(courier.name));
    });

  const delivered = (order: Order) =>
    act(async () => {
      await api.request("POST", `/device/deliveries/${order.id}/delivered`, { body: {} });
      toast(text.deliveredToast(order.code));
    });

  const statementOf = (courier: Courier) =>
    api
      .request<{ statement: Statement }>("GET", `/device/couriers/${courier.id}/statement`, { retry: true })
      .then((answer) => answer.statement);

  const showBon = (courier: Courier) =>
    act(async () => {
      const statement = await statementOf(courier);
      setOpen({ kind: "bon", statement, at: new Date().toISOString() });
    });

  const settle = async (courier: Courier) => {
    const authorization = await authorize("couriers.settle", text.settleCode);
    if (!authorization) return;
    await act(async () => {
      setOpen({ kind: "settle", statement: await statementOf(courier), authorization });
    });
  };

  /** "Ongedaan maken" (Furkan, 11/10/2026: settled by mistake), with the code of a manager or the owner. */
  const undo = async (settlement: Settlement) => {
    const authorization = await authorize("couriers.settle", text.undoCode);
    if (authorization) setOpen({ kind: "undo", settlement, authorization });
  };

  const changeMode = async (mode: CourierMode) => {
    if (!board || board.mode === mode) return;
    const authorization = await authorize("couriers.settle", text.modeCode);
    if (!authorization) return;
    await act(async () => {
      await api.request("PUT", "/device/couriers/mode", { body: { mode }, staffToken: authorization.token });
      toast(text.modeChanged(text.modes[mode]));
    });
  };

  if (!board) return <div className="panel admin-section couriers" aria-busy="true" />;
  const { open: unassigned, columns } = boardColumns(board);

  const card = (order: BoardDelivery, toAssign: boolean) => {
    const onTheWay = order.departedAt !== null;
    const pay = payPill(order);
    const km = kmText(order.km);
    return (
      <div className={`card${onTheWay ? " onroad" : ""}`} key={order.id} aria-label={order.code}>
        <b>
          <span className="code">{order.code}</span> · {formatTime(order.createdAt)} ·{" "}
          <span className="num">{formatEuro(order.totalCents)}</span>
        </b>
        <span>
          {addressOf(order) || "—"} {km !== null && <span className="proto">{km}</span>}
        </span>
        <span>
          <span className={`pill ${pay.tone}`}>{pay.text}</span>{" "}
          {onTheWay && order.eta !== null && <span className="pill">{text.arrival(formatTime(order.eta))}</span>}
        </span>
        {order.problems.length > 0 && (
          // the courier's last problem at the door (Tafo app, blok J): call the customer, or the courier
          <span className="problem">
            {text.problemOnCard(order.problems.at(-1)!.reason, formatTime(order.problems.at(-1)!.at))}
          </span>
        )}
        <div className="acts">
          {toAssign ? (
            board.couriers.map((courier) => (
              <button
                type="button"
                className="btn"
                key={courier.id}
                disabled={busy}
                onClick={() => void assign(order, courier)}
              >
                {text.assignTo(courier.name)}
              </button>
            ))
          ) : (
            <>
              {onTheWay ? (
                <button
                  type="button"
                  className="btn"
                  disabled={order.trackToken === null}
                  onClick={() => {
                    if (order.trackToken !== null) setOpen({ kind: "track", token: order.trackToken });
                  }}
                >
                  {text.track}
                </button>
              ) : (
                <button type="button" className="btn" disabled={busy} onClick={() => void assign(order, null)}>
                  {text.back}
                </button>
              )}
              <button type="button" className="btn" disabled={busy} onClick={() => void delivered(order)}>
                {text.delivered}
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="panel admin-section couriers">
      <h2>{text.title}</h2>
      <div className="cmode">
        <span className="proto">{text.modeLabel}</span>
        <div className="seg" role="group" aria-label={text.modeLabel}>
          {MODES.map((mode) => (
            <button
              type="button"
              key={mode}
              aria-pressed={board.mode === mode}
              disabled={busy}
              onClick={() => void changeMode(mode)}
            >
              {text.modes[mode]}
            </button>
          ))}
        </div>
      </div>
      <div className="cols">
        <section className="col" aria-label={text.toDeliver}>
          <h3>
            <span>{text.toDeliver}</span>
            <span className="num">{unassigned.length}</span>
          </h3>
          {unassigned.length === 0 ? (
            <span className="proto">{text.nothing}</span>
          ) : (
            unassigned.map((order) => card(order, true))
          )}
        </section>
        {columns.map((column) => {
          const { courier, rides, statement } = column;
          const count = statement?.rides ?? 0;
          return (
            <section className="col" key={courier.id} aria-label={courier.name}>
              <h3>
                <span>{courier.name}</span>
                <span className="num">{rides.length}</span>
              </h3>
              <span className="proto">{courierStatus(column)}</span>
              {rides.length === 0 ? (
                <span className="proto">{text.noRides}</span>
              ) : (
                rides.map((order) => card(order, false))
              )}
              <div className="settlebar">
                <span>{text.settleBar(count, formatEuro(statement?.cashCents ?? 0))}</span>
                <span className="sbtns">
                  <button
                    type="button"
                    className="btn"
                    disabled={busy || count === 0}
                    onClick={() => void showBon(courier)}
                  >
                    {text.bon}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    disabled={busy || count === 0}
                    onClick={() => void settle(courier)}
                  >
                    {text.settle}
                  </button>
                </span>
              </div>
            </section>
          );
        })}
      </div>
      <p className="note">
        {board.mode === "self" ? text.noteSelf : text.noteTill}
        {text.noteSettle}
      </p>
      {board.settlements.length > 0 && (
        <>
          <h3>{text.today}</h3>
          <div className="tablewrap">
            <table className="settlements stack">
              <thead>
                <tr>
                  <th>{text.columns.time}</th>
                  <th>{text.columns.courier}</th>
                  <th className="r">{text.columns.rides}</th>
                  <th className="r">{text.columns.cash}</th>
                  <th>{text.columns.by}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {board.settlements.map((settlement) => {
                  const time = formatTime(settlement.settledAt);
                  return (
                    <tr key={settlement.id} className={settlement.undone ? "undone" : undefined}>
                      <td className="num">{time}</td>
                      <td className="grow">
                        {settlement.courier.name}
                        {settlement.undone && (
                          <div className="note">
                            <span className="pill warn">{text.undone}</span>{" "}
                            {text.undoneBy(
                              settlement.undone.by,
                              formatTime(settlement.undone.at),
                              settlement.undone.reason,
                            )}
                          </div>
                        )}
                      </td>
                      <td className="r num" data-label={text.columns.rides}>
                        {settlement.rides}
                      </td>
                      <td className="r num" data-label={text.columns.cash}>
                        {formatEuro(settlement.cashCents)}
                      </td>
                      <td data-label={text.columns.by}>{settlement.settledBy}</td>
                      <td className="end">
                        <div className="rowbtns">
                          <button
                            type="button"
                            className="btn small"
                            aria-label={text.reprint(settlement.courier.name, time)}
                            onClick={() => {
                              setOpen({ kind: "bon", statement: settlement, at: settlement.settledAt });
                            }}
                          >
                            🧾
                          </button>
                          {settlement.undone === null && (
                            <button
                              type="button"
                              className="btn small"
                              aria-label={text.undoOf(settlement.courier.name, time)}
                              disabled={busy}
                              onClick={() => void undo(settlement)}
                            >
                              {text.undo}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      <p className="note">
        {text.appNote[0]}
        <b>{text.appNote[1]}</b>
        {text.appNote[2]}
      </p>

      {open?.kind === "bon" && (
        <CourierBonDialog
          statement={open.statement}
          at={open.at}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
      {open?.kind === "settle" && (
        <SettleDialog
          statement={open.statement}
          couriers={board.couriers}
          authorization={open.authorization}
          onMoved={(message) => {
            toast(message);
            void load();
          }}
          onSettled={(settlement, printed) => {
            // prototype: "Mehmet afgerekend · afrekenbon → Kassaprinter toog"
            toast(
              printed === null
                ? text.settleDialog.done(settlement.courier.name)
                : t.print.settled(settlement.courier.name, printed.printer),
            );
            // the copy of the afrekenbon, kept and printed (prototype)
            setOpen({ kind: "bon", statement: settlement, at: settlement.settledAt });
            void load();
          }}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
      {open?.kind === "undo" && (
        <UndoSettlementDialog
          settlement={open.settlement}
          authorization={open.authorization}
          onUndone={(settlement) => {
            toast(text.undoDialog.done(settlement.courier.name));
            setOpen(null);
            void load();
          }}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
      {open?.kind === "track" && (
        <TrackDialog
          token={open.token}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
    </div>
  );
}
